import { catalogo as apiCatalogo } from './servicios'
import type { ProductoCache } from './tipos'

/**
 * Catálogo del local, en memoria.
 *
 * Existe por una sola razón: que escanear un código no dependa de un viaje al
 * servidor. Con la tienda en Chile y el servidor fuera del país, cada escaneo
 * pagaba ese viaje antes de poder pintar siquiera el nombre del producto.
 *
 * **Lo que hay aquí es para mostrar, no para cobrar.** El precio con que se
 * cobra lo resuelve el servidor al agregar cada línea y otra vez al cerrar la
 * venta. Si el dueño cambió un precio después de que la tablet bajó el
 * catálogo, el cajero ve el viejo por un instante y el del servidor lo corrige
 * en cuanto responde. Nunca al revés: lo que se cobra jamás sale de aquí.
 *
 * No se guarda en el navegador a propósito. En memoria se pierde al recargar y
 * se vuelve a bajar, que son unos cientos de milisegundos una vez por turno;
 * guardado en disco habría que decidir cuándo invalidarlo, y un catálogo viejo
 * que sobrevive a un cierre de sesión es peor que no tener catálogo.
 */

let porCodigo = new Map<string, ProductoCache>()
let generadoEn: string | null = null
let cargando: Promise<void> | null = null

/** Normaliza para comparar: los lectores a veces mandan espacios al final. */
function clave(codigo: string): string {
    return codigo.trim().toUpperCase()
}

/**
 * Descarga el catálogo. Si ya hay una descarga en curso, se engancha a ella en
 * vez de pedir otra: el POS la dispara al montar y el cajero puede escanear
 * antes de que termine.
 */
export function cargarCatalogo(): Promise<void> {
    if (cargando) return cargando

    cargando = apiCatalogo
        .catalogoPos()
        .then((datos) => {
            const mapa = new Map<string, ProductoCache>()

            for (const producto of datos.productos) {
                // El SKU también resuelve: el cajero lo teclea cuando la
                // etiqueta está rota y el lector no toma el código.
                mapa.set(clave(producto.sku), producto)

                for (const codigo of producto.codigos) {
                    mapa.set(clave(codigo), producto)
                }
            }

            porCodigo = mapa
            generadoEn = datos.generadoEn
        })
        .catch(() => {
            // Sin catálogo el POS sigue funcionando: cada escaneo vuelve a
            // preguntarle al servidor, que es como funcionaba antes.
        })
        .finally(() => {
            cargando = null
        })

    return cargando
}

/** El producto de ese código, o nulo si el catálogo no lo conoce. */
export function buscarPorCodigo(codigo: string): ProductoCache | null {
    return porCodigo.get(clave(codigo)) ?? null
}

export function hayCatalogo(): boolean {
    return porCodigo.size > 0
}

/** Cuándo se generó lo que tenemos. Para avisar si quedó viejo. */
export function generadoDelCatalogo(): string | null {
    return generadoEn
}

/** Se vacía al cerrar sesión: el siguiente turno puede ser de otro local. */
export function olvidarCatalogo(): void {
    porCodigo = new Map()
    generadoEn = null
}
